/*
  Lazily load video.js stuff from main.js when it's detected to be needed.
  Each player is initialized individually when it enters the viewport, so
  video-heavy pages don't overwhelm low-memory devices (especially Android).
*/

import { findAll, whenSelectorPresentAll } from '../../../common/helpers/dom'
import { sizeWrapper } from './wrapper'
import './placeholder.scss'
import { onMobileAppChromecastLoadedIfAvailable } from './custom_plugins/mobile_app_chromecast'
import { lazyInitFacade, setDisposeOnPromote, resetFacades } from './facade'

// On builder pages with many videos (>6), keep every video as a lightweight click-to-load facade
// (videojs/facade.js) so we never initialize dozens of players at once — that crashed mobile
// browsers. With 6 or fewer, fall back to the previous lazy-on-scroll behavior so small pages are
// unchanged. The threshold lives here because only this layer sees the whole page at once.
const FACADE_THRESHOLD = 6
if (window.onPageLoad) {
  window.onPageLoad('videojs-facades', () => {
    resetFacades()
    const facades = findAll('.video-facade[data-facade]')
    if (facades.length === 0) return

    const many = facades.length > FACADE_THRESHOLD
    setDisposeOnPromote(many)
    if (!many) facades.forEach((facade) => lazyInitFacade(facade))

    // Warm the heavy player bundle in the background so the first tap's `import('./main.js')`
    // resolves from cache (a microtask) instead of a network fetch — keeping the promote within
    // the user-gesture activation window so playback isn't blocked (no extra tap). Idle to avoid
    // competing with the initial page render.
    const warm = () => import('./main.js')
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warm)
    } else {
      setTimeout(warm, 2000)
    }
  })
}

const isInHiddenModal = (element) => {
  const modal = element.closest('.t-modal')
  if (!modal) return false

  return modal.getAttribute('aria-hidden') !== 'false'
}

const initializeSingleVideo = async (video) => {
  const { initializeVideoPlayer } = await import('./main.js')
  onMobileAppChromecastLoadedIfAvailable(() => initializeVideoPlayer(video))
}

const initializeSingleAudio = async (audio) => {
  const { initializeAudioPlayer } = await import('./main.js')
  initializeAudioPlayer(audio)
}

const initializeElement = (el) => {
  if (el.tagName === 'VIDEO') {
    initializeSingleVideo(el)
  } else {
    initializeSingleAudio(el)
  }
}

const initializeAllVideos = async () => {
  const { initializeVideoPlayer } = await import('./main.js')
  onMobileAppChromecastLoadedIfAvailable(() => {
    findAll('video:not(.no-video-js):not(.video-js--initialized)').forEach(
      (video) => {
        if (!isInHiddenModal(video)) initializeVideoPlayer(video)
      }
    )
  })
}

const initializeAllAudios = async () => {
  const { initializeAudioPlayer } = await import('./main.js')
  findAll(
    'audio:not(.jp-audio):not(.no-video-js):not(.video-js--initialized)'
  ).forEach((audio) => {
    if (!isInHiddenModal(audio)) initializeAudioPlayer(audio)
  })
}

window.initializeAllVideojs = () => {
  initializeAllVideos()
  initializeAllAudios()
}

// Classic simplero.js (embedded landing pages) injects markup without triggering onDomChanged,
// so it calls this to start the players inside its own container only.
window.initializeVideojsWithin = (root) => {
  root
    .querySelectorAll('video:not(.no-video-js):not(.video-js--initialized)')
    .forEach((video) => {
      if (!isInHiddenModal(video)) initializeSingleVideo(video)
    })
  root
    .querySelectorAll(
      'audio:not(.jp-audio):not(.no-video-js):not(.video-js--initialized)'
    )
    .forEach((audio) => {
      if (!isInHiddenModal(audio)) initializeSingleAudio(audio)
    })
}

// Initialize videos and audios in modals when they are shown
document.addEventListener('modal:shown', (e) => {
  const modal = e.target
  if (!modal.classList.contains('t-modal')) return

  const videos = modal.querySelectorAll(
    'video:not(.no-video-js):not(.video-js--initialized)'
  )
  const audios = modal.querySelectorAll(
    'audio:not(.jp-audio):not(.no-video-js):not(.video-js--initialized)'
  )

  videos.forEach((video) => initializeSingleVideo(video))
  audios.forEach((audio) => initializeSingleAudio(audio))
})

// Initialize if it's added after load. Same .no-video-js opt-out every other entry
// point here honors — without it, opting a plain <audio>/<video> out only worked
// until the next DOM change swept it back up into a player.
if (window.onDomChanged) {
  window.onDomChanged('video:not(.no-video-js)', function () {
    if (!isInHiddenModal(this[0])) initializeSingleVideo(this[0])
  })
  window.onDomChanged('audio:not(.no-video-js)', function () {
    if (!isInHiddenModal(this[0])) initializeSingleAudio(this[0])
  })
}

// Each player is initialized lazily when it enters the viewport (with a 400px margin).
// This avoids the previous behavior of initializing ALL players at once, which overwhelmed
// low-memory Android devices on video-heavy pages.
whenSelectorPresentAll(
  'video:not(.no-video-js):not(.video-js--initialized), audio:not(.jp-audio):not(.no-video-js):not(.video-js--initialized)',
  (el) => {
    if (isInHiddenModal(el)) return

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect()
            initializeElement(el)
          }
        },
        { rootMargin: '400px' }
      )

      observer.observe(el)
      sizeWrapper(el)
    } else {
      initializeElement(el)
    }
  }
)
